// ⏱️ Countdown - Compte à rebours dynamique vers la prochaine édition
// Affiche le temps restant en jours, heures, minutes et secondes
// Utilisé dans : src/pages/HomePage.jsx

import { useState, useEffect } from 'react'
import { Calendar, Clock } from 'lucide-react'

/**
 * Composant de compte à rebours vers l'événement Rock'In Agde
 * Calcule et affiche le temps restant en temps réel avec mise à jour toutes les secondes
 * Affiche un message alternatif une fois l'événement passé
 * 
 * @param {Object} props - Propriétés du composant
 * @param {string} props.targetDate - Date cible au format ISO (ex: "2026-06-13T21:00:00")
 */
export default function Countdown({ targetDate }) {
  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft())

  function calculateTimeLeft() {
    const difference = +new Date(targetDate) - +new Date()
    
    if (difference > 0) {
      return {
        jours: Math.floor(difference / (1000 * 60 * 60 * 24)),
        heures: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        secondes: Math.floor((difference / 1000) % 60)
      }
    }
    
    return { jours: 0, heures: 0, minutes: 0, secondes: 0 }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearTimeout(timer)
  })

  const isEventPassed = timeLeft.jours === 0 && timeLeft.heures === 0 && timeLeft.minutes === 0 && timeLeft.secondes === 0

  if (isEventPassed) {
    return (
      <section className="section-padding bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
        <div className="container-custom text-center">
          <Calendar className="w-16 h-16 mx-auto mb-6 text-primary-200" />
          <h2 className="text-4xl md:text-5xl font-display font-bold mb-4">
            L'événement est terminé !
          </h2>
          <p className="text-xl text-primary-100">
            Merci d'avoir participé au Rock'In Agde. Rendez-vous l'année prochaine !
          </p>
        </div>
      </section>
    )
  }

  return (
    <section className="section-padding bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
      <div className="container-custom">
        <div className="max-w-5xl mx-auto text-center">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Clock className="w-8 h-8 text-primary-200" />
              <span className="text-sm font-medium uppercase tracking-wider text-primary-200">
                Prochaine édition
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-display font-bold mb-4">
              Plus que...
            </h2>
            <p className="text-xl text-primary-100">
              13 Juin 2026 à 21h · La Promenade d'Agde
            </p>
          </div>

          {/* Countdown */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { label: 'Jours', value: timeLeft.jours },
              { label: 'Heures', value: timeLeft.heures },
              { label: 'Minutes', value: timeLeft.minutes },
              { label: 'Secondes', value: timeLeft.secondes }
            ].map((item, index) => (
              <div
                key={index}
                className="relative"
              >
                {/* Card */}
                <div className="bg-white/10 backdrop-blur-sm border-2 border-white/20 rounded-2xl p-6 md:p-8 hover:bg-white/15 hover:scale-105 transition-all shadow-xl">
                  {/* Value */}
                  <div className="text-5xl md:text-7xl font-display font-bold mb-2 tabular-nums">
                    {String(item.value).padStart(2, '0')}
                  </div>
                  {/* Label */}
                  <div className="text-sm md:text-base uppercase tracking-wider text-primary-100 font-medium">
                    {item.label}
                  </div>
                </div>

                {/* Separator */}
                {index < 3 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2 text-4xl font-bold text-white/30">
                    :
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-12">
            <a
              href="https://tally.so/r/nPaZJ0"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-600 hover:bg-primary-50 rounded-lg font-bold text-lg transition-all shadow-xl hover:scale-105"
            >
              <Calendar size={24} />
              Participer au casting
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
